import { useEffect, useState } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { showNotification } from "context/notification.service";
import { pennylaneApi, type PennylaneStatus } from "@services/api/pennylane";
import { useDataStore } from "@store/user_org";

/**
 * Inline Pennylane connection card rendered at the top of a project's
 * Facturation tab. Lets admins paste a Personal Access Token + company ID
 * straight from the billing context — no separate settings page detour.
 *
 * Behaviour:
 *  - Disconnected → fully-expanded form with PAT + company_id inputs.
 *  - Connected → collapsed card showing only the company_id, with
 *    "Modifier" / "Déconnecter" actions. The PAT is never re-displayed.
 *  - Hidden entirely for non-admins.
 */
export function PennylaneCredentialsCard({
  isAdmin,
  subtle = false,
}: {
  isAdmin: boolean;
  /** When true, the disconnected state stays neutral white (used inside
   *  Paramètres where the card is one of many settings sections and an
   *  attention-grabbing amber background looks out of place). Defaults to
   *  amber-on-disconnected for the in-context billing surfaces where the
   *  admin needs a clear "configure me" prompt. */
  subtle?: boolean;
}) {
  const pennylaneStatus = useDataStore((s) => s.pennylaneStatus);
  const setPennylaneStatus = useDataStore((s) => s.setPennylaneStatus);
  const fetchPennylaneStatus = useDataStore((s) => s.fetchPennylaneStatus);

  const [pat, setPat] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [editing, setEditing] = useState(false);

  // Refresh on mount so an admin re-opening the tab after configuring on
  // another device sees the up-to-date state.
  useEffect(() => {
    if (isAdmin && pennylaneStatus === null) {
      fetchPennylaneStatus();
    }
  }, [isAdmin]);

  useEffect(() => {
    setCompanyId(pennylaneStatus?.company_id || "");
  }, [pennylaneStatus?.company_id]);

  if (!isAdmin) return null;

  const connected = !!pennylaneStatus?.connected;
  const showForm = !connected || editing;

  // In non-subtle mode (Suivi de facturation, per-project Facturation tab)
  // the card is purely a "configure me" prompt — once Pennylane is wired up,
  // it becomes noise and we hide the whole card. The subtle variant
  // (Paramètres → Facturation) keeps the connected state visible so admins
  // can still see / modify / disconnect the integration.
  if (connected && !subtle) return null;

  const handleSave = async () => {
    if (!pat.trim() || !companyId.trim()) {
      showNotification({
        type: "error",
        message: "Le token et l'ID d'entreprise sont obligatoires.",
      });
      return;
    }
    setSaving(true);
    try {
      const next = await pennylaneApi.saveCredentials({
        pat: pat.trim(),
        company_id: companyId.trim(),
      });
      setPennylaneStatus(next);
      setPat("");
      setEditing(false);
      showNotification({ type: "success", message: "Pennylane connecté avec succès." });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showNotification({
        type: "error",
        message: typeof detail === "string" ? detail : "Erreur lors de l'enregistrement.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const next = await pennylaneApi.clearCredentials();
      setPennylaneStatus(next);
      setCompanyId("");
      setPat("");
      showNotification({ type: "success", message: "Pennylane déconnecté." });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showNotification({
        type: "error",
        message: typeof detail === "string" ? detail : "Erreur lors de la déconnexion.",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  // Two visual modes:
  //  - subtle (Paramètres → Facturation) matches the "Copie des factures" /
  //    "Alertes facturation" sections on the same page: contained white
  //    card, neutral typography, no attention chrome.
  //  - default (Suivi de facturation, per-project Facturation tab) calls
  //    attention when disconnected with an amber background + chip, because
  //    the admin is in the billing context and needs a clear prompt.
  const wrapperClass = subtle
    ? "flex flex-col gap-2 mt-8 bg-white max-w-900 p-4 rounded-lg border-1"
    : `flex flex-col gap-3 p-4 rounded-lg border-1 mb-3 ${
        connected ? "bg-white" : "bg-amber-50 border-amber-200"
      }`;

  return (
    <div className={wrapperClass}>
      <div className="flex flex-row items-center gap-2">
        <div
          className={
            subtle
              ? "text-h2 font-semibold text-neutral-700"
              : "text-h3 font-semibold text-neutral-700"
          }
        >
          Connexion à Pennylane
        </div>
        {connected && !editing && (
          <span className="text-h5 text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
            Connectée
          </span>
        )}
        {!connected && !subtle && (
          <span className="text-h5 text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
            Non configurée
          </span>
        )}
      </div>

      {!connected && (
        <div className={subtle ? "text-h4 text-neutral-400" : "text-h4 text-neutral-600"}>
          Pour activer la facturation, générez un token API dans Pennylane :{" "}
          <b>Paramètres → Connectivité → Développeurs</b>, bouton{" "}
          <b>« Générer un token API »</b>. Cochez tous les scopes — choisissez{" "}
          <b>« Lecture et écriture »</b> quand c'est disponible, sinon{" "}
          <b>« Lecture seule »</b>. La section <b>« API V1 obsolète »</b> peut
          être ignorée. Pour la <b>Période de validité</b>, sélectionnez{" "}
          <b>« Illimitée »</b> pour éviter que le token expire. Renseignez
          aussi l'ID d'entreprise visible dans l'URL Pennylane (
          <code className="text-h5">app.pennylane.com/companies/<b>123456</b>/…</code>
          ).
        </div>
      )}

      {connected && !editing && (
        <div className={subtle ? "text-h4 text-neutral-400" : "text-h4 text-neutral-500"}>
          ID d'entreprise : <b>{pennylaneStatus?.company_id || "—"}</b>. Le
          token reste stocké côté serveur.
        </div>
      )}

      {showForm && (
        <>
          <TextField
            label="Personal Access Token *"
            value={pat}
            onChange={setPat}
            placeholder="Collez votre token Pennylane"
            type="password"
          />
          <TextField
            label="ID d'entreprise Pennylane *"
            value={companyId}
            onChange={setCompanyId}
            placeholder="156412"
          />
          <div className="flex flex-row gap-2">
            <Button
              variant="small"
              label={saving ? "Vérification..." : connected ? "Mettre à jour" : "Connecter"}
              onClick={handleSave}
              disabled={saving || !pat.trim() || !companyId.trim()}
            />
            {editing && (
              <SecondaryButton
                variant="small"
                label="Annuler"
                onClick={() => {
                  setEditing(false);
                  setPat("");
                  setCompanyId(pennylaneStatus?.company_id || "");
                }}
              />
            )}
          </div>
        </>
      )}

      {connected && !editing && (
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" label="Modifier" onClick={() => setEditing(true)} />
          <SecondaryButton
            variant="small"
            danger
            label={disconnecting ? "Déconnexion..." : "Déconnecter"}
            onClick={handleDisconnect}
            disabled={disconnecting}
          />
        </div>
      )}
    </div>
  );
}
